import React, { useEffect, useMemo, useRef, useState } from "react";

const formatTime = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  const seconds = Number(value);
  const minutes = Math.floor(seconds / 60);
  const remainder = (seconds % 60).toFixed(2).padStart(5, "0");
  return minutes > 0 ? `${minutes}:${remainder}` : remainder;
};

const formatDrop = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  const parsed = Number(value);
  return parsed >= 0
    ? `${parsed.toFixed(2)} sec`
    : `-${Math.abs(parsed).toFixed(2)} sec`;
};

const formatSeconds = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  const parsed = Number(value);
  return parsed >= 0
    ? `${parsed.toFixed(2)} sec`
    : `-${Math.abs(parsed).toFixed(2)} sec`;
};

const formatPoints = (value) => Number(value || 0).toFixed(2);

const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;

const parseMeetDisplayParts = (sourceValue, fallbackId) => {
  const raw = String(sourceValue || "").trim();
  if (!raw) {
    return {
      meetDate: "",
      meetTitle: String(fallbackId || "Meet").trim(),
    };
  }

  const normalized = raw
    .replace(/^Results\s+/i, "")
    .replace(/\.[^./\\]+$/, "")
    .trim();

  const dateMatch = normalized.match(/(\d{4}-\d{2}-\d{2})/);
  const meetDate = dateMatch?.[1] || "";

  let meetTitle = normalized;
  if (meetDate) {
    meetTitle = normalized
      .slice(normalized.indexOf(meetDate) + meetDate.length)
      .trim();
  }

  meetTitle = meetTitle
    .replace(/\s+_[^_]+_\s+\d+$/i, "")
    .replace(/\s+[A-Z0-9]+\s+\d+$/i, "")
    .trim();

  return {
    meetDate,
    meetTitle: meetTitle || String(fallbackId || "Meet").trim(),
  };
};

const formatMeetDisplay = (meet) => {
  if (!meet) return "Meet";

  const explicitDate = String(meet.meet_date || "").trim();
  const explicitTitle = String(meet.meet_title || "").trim();
  if (explicitDate && explicitTitle) {
    return `${explicitDate} • ${explicitTitle}`;
  }

  const currentLabel = String(meet.meet_label || "").trim();
  if (currentLabel.includes(" • ")) {
    return currentLabel;
  }

  const { meetDate, meetTitle } = parseMeetDisplayParts(
    meet.source_file || currentLabel,
    meet.meet_id,
  );

  if (meetDate && meetTitle) {
    return `${meetDate} • ${meetTitle}`;
  }

  return (
    explicitTitle || meetTitle || currentLabel || String(meet.meet_id || "Meet")
  );
};

const formatMeetName = (meet) => {
  if (!meet) return "Meet";

  const explicitTitle = String(meet.meet_title || "").trim();
  if (explicitTitle) return explicitTitle;

  const currentLabel = String(meet.meet_label || "").trim();
  if (currentLabel.includes(" • ")) {
    return (
      currentLabel.split(" • ").slice(1).join(" • ").trim() || currentLabel
    );
  }

  const { meetTitle } = parseMeetDisplayParts(
    meet.source_file || currentLabel,
    meet.meet_id,
  );

  return meetTitle || currentLabel || String(meet.meet_id || "Meet");
};

const formatMeetAbbreviation = (meet) => {
  if (!meet) return "MEET";

  const explicit = String(meet.meet_abbr || meet.meet_code || "")
    .trim()
    .toUpperCase();
  if (explicit) return explicit;

  const candidates = [
    String(meet.meet_title || ""),
    String(meet.meet_label || ""),
    String(meet.source_file || ""),
  ].filter(Boolean);

  for (const candidate of candidates) {
    const atMatch = candidate.match(/\b([A-Z]{3,5})\s+at\s+([A-Z]{3,5})\b/i);
    if (atMatch?.[2]) return atMatch[2].toUpperCase();

    const underscoreMatch = candidate.match(/_([A-Z0-9]{3,6})_/);
    if (underscoreMatch?.[1]) return underscoreMatch[1].toUpperCase();

    const tokenMatch = candidate.match(/\b[A-Z]{3,5}\b/);
    if (tokenMatch?.[0]) return tokenMatch[0].toUpperCase();
  }

  return "MEET";
};

const formatMeetMonthDay = (meet) => {
  if (!meet) return "—";

  const explicitDate = String(meet.meet_date || "").trim();
  const parsedSourceDate = parseMeetDisplayParts(
    meet.source_file || meet.meet_label,
    meet.meet_id,
  ).meetDate;

  const dateText = explicitDate || parsedSourceDate;
  if (!dateText) return "—";

  const parsedDate = new Date(`${dateText}T00:00:00`);
  if (Number.isNaN(parsedDate.getTime())) return "—";

  const month = parsedDate
    .toLocaleString("en-US", { month: "short" })
    .toUpperCase();
  const day = String(parsedDate.getDate()).padStart(2, "0");

  return `${month} ${day}`;
};

const getMeetTimeValue = (meet) => {
  if (!meet) return 0;

  const explicitDate = String(meet.meet_date || "").trim();
  if (explicitDate) {
    const parsed = Date.parse(explicitDate);
    if (!Number.isNaN(parsed)) return parsed;
  }

  const fromSource = parseMeetDisplayParts(
    meet.source_file || meet.meet_label,
    meet.meet_id,
  ).meetDate;
  if (fromSource) {
    const parsed = Date.parse(fromSource);
    if (!Number.isNaN(parsed)) return parsed;
  }

  const year = Number(meet.meet_year || 0);
  return Number.isFinite(year) ? year * 1000 : 0;
};

const buildPotentialPointsByYear = (meetSummaries = []) => {
  const totals = new Map();

  meetSummaries.forEach((meet) => {
    const year = String(meet?.meet_year || "").trim();
    const rawPoints = Number(meet?.raw_points);
    if (!year || !Number.isFinite(rawPoints)) return;
    totals.set(year, (totals.get(year) || 0) + rawPoints);
  });

  return Array.from(totals.entries())
    .map(([year, points]) => ({ year, points }))
    .sort((a, b) => Number(b.year) - Number(a.year));
};

const getProjectedMeetPoints = (meetSummaries = []) => {
  const values = meetSummaries
    .map((meet) => Number(meet?.raw_points))
    .filter((value) => Number.isFinite(value));

  if (!values.length) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

const TrendChart = ({ meets = [] }) => {
  const [selectedMeetKey, setSelectedMeetKey] = useState("");
  const chartRef = useRef(null);

  useEffect(() => {
    if (!meets.length) {
      setSelectedMeetKey("");
    } else {
      const hasSelection = meets.some(
        (meet) => (meet.meet_id || meet.meet_label) === selectedMeetKey,
      );
      if (!hasSelection) {
        setSelectedMeetKey("");
      }
    }
  }, [meets, selectedMeetKey]);

  useEffect(() => {
    if (!selectedMeetKey) return;

    const onClickOutside = (event) => {
      if (!chartRef.current) return;
      if (!chartRef.current.contains(event.target)) {
        setSelectedMeetKey("");
      }
    };

    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [selectedMeetKey]);

  if (!meets.length) {
    return (
      <p className="swimmer-tracker-empty-text">No meet trend data yet.</p>
    );
  }

  const width = 720;
  const height = 220;
  const paddingX = 34;
  const paddingTop = 28;
  const paddingBottom = 54;
  const chartHeight = height - paddingTop - paddingBottom;
  const chartWidth = width - paddingX * 2;
  const values = meets.map((item) => Number(item.raw_points || 0));
  const minValue = Math.min(...values, 0);
  const maxValue = Math.max(...values, 1);
  const range = maxValue - minValue || 1;

  const points = meets.map((meet, index) => {
    const x =
      meets.length === 1
        ? width / 2
        : paddingX + (index * chartWidth) / (meets.length - 1);
    const y =
      paddingTop +
      ((maxValue - Number(meet.raw_points || 0)) / range) * chartHeight;
    return { x, y, meet };
  });

  const selectedMeet = meets.find(
    (meet) => (meet.meet_id || meet.meet_label) === selectedMeetKey,
  );
  const selectedPoint = points.find(
    (point) =>
      (point.meet.meet_id || point.meet.meet_label) === selectedMeetKey,
  );

  const selectedPointPercent = selectedPoint
    ? (selectedPoint.x / width) * 100
    : 0;

  const detailCardPosition = selectedPoint
    ? selectedPointPercent <= 24
      ? {
          left: "0.6rem",
          right: "auto",
          transform: "none",
          top: `${selectedPoint.y + 18}px`,
        }
      : selectedPointPercent >= 76
        ? {
            left: "auto",
            right: "0.6rem",
            transform: "none",
            top: `${selectedPoint.y + 18}px`,
          }
        : {
            left: `${selectedPointPercent}%`,
            right: "auto",
            transform: "translateX(-50%)",
            top: `${selectedPoint.y + 18}px`,
          }
    : null;

  const polyline = points.map((point) => `${point.x},${point.y}`).join(" ");

  return (
    <div
      className="swimmer-tracker-chart-wrap"
      ref={chartRef}
      onClick={() => setSelectedMeetKey("")}
    >
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="swimmer-tracker-chart-svg"
      >
        <line
          x1={paddingX}
          y1={height - paddingBottom}
          x2={width - paddingX}
          y2={height - paddingBottom}
          stroke="rgba(120, 150, 216, 0.35)"
          strokeWidth="1"
        />
        <polyline
          fill="none"
          stroke="#00f5d4"
          strokeWidth="4"
          strokeLinejoin="round"
          strokeLinecap="round"
          points={polyline}
        />

        {points.map((point) => (
          <g key={point.meet.meet_id || point.meet.meet_label}>
            <circle
              cx={point.x}
              cy={point.y}
              r="6.5"
              fill="#02061a"
              stroke="#7ff9ea"
              strokeWidth="3"
              className="swimmer-tracker-chart-point-btn"
              onClick={(event) => {
                event.stopPropagation();
                setSelectedMeetKey(point.meet.meet_id || point.meet.meet_label);
              }}
            />
            <text
              x={point.x}
              y={point.y - 13}
              textAnchor="middle"
              fill="#eaf3ff"
              fontSize="13"
              fontWeight="700"
            >
              {formatPoints(point.meet.raw_points)}
            </text>
            <text
              x={point.x}
              y={height - 28}
              textAnchor="middle"
              fill="#8fb0e7"
              fontSize="11"
              fontWeight="700"
            >
              {point.meet.meet_year || "—"}
            </text>
            <text
              x={point.x}
              y={height - 12}
              textAnchor="middle"
              fill="#a8fff0"
              fontSize="10"
              fontWeight="700"
            >
              {formatDrop(point.meet.average_drop)}
            </text>
          </g>
        ))}
      </svg>

      <div className="swimmer-tracker-chart-legend-list">
        {meets.map((meet) => (
          <div
            key={`legend-${meet.meet_id || meet.meet_label}`}
            className="swimmer-tracker-chart-legend-item"
          >
            <strong className="swimmer-tracker-chart-legend-title">
              {formatMeetName(meet)}
            </strong>
            <span className="swimmer-tracker-chart-legend-meta">
              {meet.events} events • {formatPoints(meet.raw_points)} potential
              pts • drop {formatDrop(meet.average_drop)}
            </span>
          </div>
        ))}
      </div>

      {selectedMeet && selectedPoint ? (
        <div
          className="swimmer-tracker-trend-detail-card"
          style={detailCardPosition || undefined}
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            className="swimmer-tracker-trend-detail-close"
            onClick={() => setSelectedMeetKey("")}
            aria-label="Close meet detail"
          >
            ×
          </button>
          <strong className="swimmer-tracker-trend-detail-title">
            {formatMeetName(selectedMeet)} breakdown
          </strong>
          <div className="swimmer-tracker-trend-detail-grid">
            <div className="swimmer-tracker-trend-detail-block">
              <span className="swimmer-tracker-trend-detail-label">
                Potential points
              </span>
              <span className="swimmer-tracker-trend-detail-value">
                {formatPoints(selectedMeet.raw_points)}
              </span>
              <span className="swimmer-tracker-trend-detail-meta">
                Place: {formatPoints(selectedMeet.potential_place_points)} •
                Drop: {formatPoints(selectedMeet.potential_drop_points)}
              </span>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const EventTrendPopup = ({ entries = [] }) => {
  const chartEntries = entries.filter((entry) =>
    Number.isFinite(Number(entry.finalTimeNum)),
  );

  if (!chartEntries.length) {
    return (
      <div className="swimmer-tracker-event-trend-popup">
        <p className="swimmer-tracker-empty-text">
          No timed swims available for this event yet.
        </p>
      </div>
    );
  }

  const width = 420;
  const height = 155;
  const paddingX = 24;
  const paddingTop = 20;
  const paddingBottom = 34;
  const chartHeight = height - paddingTop - paddingBottom;
  const chartWidth = width - paddingX * 2;

  const values = chartEntries.map((entry) => Number(entry.finalTimeNum || 0));
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const range = maxValue - minValue || 1;

  const points = chartEntries.map((entry, index) => {
    const x =
      chartEntries.length === 1
        ? width / 2
        : paddingX + (index * chartWidth) / (chartEntries.length - 1);
    const y =
      paddingTop +
      ((maxValue - Number(entry.finalTimeNum || 0)) / range) * chartHeight;

    return { x, y, entry };
  });

  const polyline = points.map((point) => `${point.x},${point.y}`).join(" ");

  return (
    <div className="swimmer-tracker-event-trend-popup">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="swimmer-tracker-event-trend-svg"
      >
        <line
          x1={paddingX}
          y1={height - paddingBottom}
          x2={width - paddingX}
          y2={height - paddingBottom}
          stroke="rgba(120, 150, 216, 0.35)"
          strokeWidth="1"
        />
        <polyline
          fill="none"
          stroke="#00f5d4"
          strokeWidth="3"
          strokeLinejoin="round"
          strokeLinecap="round"
          points={polyline}
        />

        {points.map((point) => (
          <g key={point.entry.id}>
            <circle
              cx={point.x}
              cy={point.y}
              r="5.5"
              fill="#02061a"
              stroke="#7ff9ea"
              strokeWidth="2.4"
            />
            <text
              x={point.x}
              y={point.y - 10}
              textAnchor="middle"
              fill="#eaf3ff"
              fontSize="10"
              fontWeight="700"
            >
              {formatTime(point.entry.finalTime)}
            </text>
            <text
              x={point.x}
              y={height - 12}
              textAnchor="middle"
              fill="#8fb0e7"
              fontSize="9"
              fontWeight="700"
            >
              {point.entry.meetAbbreviation}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
};

const SwimmerTracker = ({ swimmer, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedYear, setSelectedYear] = useState("");
  const [showYearPopup, setShowYearPopup] = useState(false);
  const [selectedEventTrendKey, setSelectedEventTrendKey] = useState("");
  const yearPopupRef = useRef(null);
  const swimmerData = swimmer || {};

  const sortedResults = useMemo(
    () =>
      [...(swimmerData.results || [])].sort((a, b) => {
        const yearDiff = Number(a.meet_year || 0) - Number(b.meet_year || 0);
        if (yearDiff !== 0) return yearDiff;
        return String(a.meet_label || "").localeCompare(
          String(b.meet_label || ""),
        );
      }),
    [swimmerData.results],
  );

  const meetSummaries = useMemo(
    () => swimmerData.meet_summaries || [],
    [swimmerData.meet_summaries],
  );

  const orderedYearlyTotals = useMemo(
    () => buildPotentialPointsByYear(meetSummaries),
    [meetSummaries],
  );

  const eventGroups = useMemo(() => {
    const grouped = new Map();

    sortedResults.forEach((result, index) => {
      const eventName = String(result.event || "Unknown Event").trim();
      const eventKey = eventName.toLowerCase();
      const finalTimeNum = Number(result.final_time);
      const timeValue = getMeetTimeValue(result);
      const meetName = formatMeetName(result);
      const meetAbbreviation = formatMeetAbbreviation(result);
      const rawPoints = Number(result.raw_fantasy_points);
      const scoredPoints = Number(result.fantasy_points);
      const displayPoints = Number.isFinite(rawPoints)
        ? rawPoints
        : Number.isFinite(scoredPoints)
          ? scoredPoints
          : 0;

      const entry = {
        id: `${result.meet_id || result.meet_label || "meet"}-${eventName}-${index}`,
        meetName,
        meetAbbreviation,
        finalTime: result.final_time,
        finalTimeNum,
        baselineTime: result.baseline_time,
        drop: result.time_drop,
        points: displayPoints,
        status: result.is_active ? "Active" : "Bench",
        timeValue,
        sortFallback: index,
      };

      if (!grouped.has(eventKey)) {
        grouped.set(eventKey, {
          eventKey,
          eventName,
          entries: [],
        });
      }

      grouped.get(eventKey).entries.push(entry);
    });

    return Array.from(grouped.values())
      .map((group) => {
        const entries = [...group.entries].sort((a, b) => {
          const timeDiff = a.timeValue - b.timeValue;
          if (timeDiff !== 0) return timeDiff;
          return a.sortFallback - b.sortFallback;
        });

        const bestEntry = entries.reduce((best, current) => {
          if (!Number.isFinite(current.finalTimeNum)) return best;
          if (!best) return current;
          return current.finalTimeNum < best.finalTimeNum ? current : best;
        }, null);

        return {
          ...group,
          entries,
          bestTime: bestEntry?.finalTime,
        };
      })
      .sort((a, b) => a.eventName.localeCompare(b.eventName));
  }, [sortedResults]);

  const projectedSeasonPoints = Number(orderedYearlyTotals[0]?.points || 0);
  const projectedMeetPoints = useMemo(
    () => getProjectedMeetPoints(meetSummaries),
    [meetSummaries],
  );

  const bestDropResult = useMemo(() => {
    return sortedResults.reduce((best, result) => {
      const drop = Number(result.time_drop);
      if (Number.isNaN(drop)) {
        return best;
      }
      if (!best) return result;
      return drop > Number(best.time_drop || 0) ? result : best;
    }, null);
  }, [sortedResults]);

  const attendanceByYear = useMemo(() => {
    const byYear = {};
    const seen = new Set();
    meetSummaries.forEach((meet) => {
      const year = meet?.meet_year != null ? String(meet.meet_year) : "";
      const meetKey = meet?.meet_id || meet?.meet_label;
      if (!year || !meetKey) return;
      const composite = `${year}::${meetKey}`;
      if (seen.has(composite)) return;
      seen.add(composite);
      byYear[year] = (byYear[year] || 0) + 1;
    });
    return byYear;
  }, [meetSummaries]);

  const attendanceYears = useMemo(() => {
    return Object.keys(attendanceByYear).sort((a, b) => Number(a) - Number(b));
  }, [attendanceByYear]);

  const priorYear = attendanceYears.length
    ? attendanceYears.length > 1
      ? attendanceYears[attendanceYears.length - 2]
      : attendanceYears[attendanceYears.length - 1]
    : "";

  const selectedYearMeetBreakdown = useMemo(() => {
    if (!selectedYear) return [];
    return [...meetSummaries]
      .filter((meet) => String(meet.meet_year || "") === String(selectedYear))
      .sort((a, b) => {
        const uploadDiff =
          Number(a.uploaded_at || 0) - Number(b.uploaded_at || 0);
        if (uploadDiff !== 0) return uploadDiff;
        return String(a.meet_label || "").localeCompare(
          String(b.meet_label || ""),
        );
      });
  }, [meetSummaries, selectedYear]);

  useEffect(() => {
    if (!orderedYearlyTotals.length) {
      setSelectedYear("");
      setShowYearPopup(false);
      return;
    }

    const hasCurrentSelection = orderedYearlyTotals.some(
      (entry) => String(entry.year) === String(selectedYear),
    );
    if (!hasCurrentSelection) {
      setSelectedYear(String(orderedYearlyTotals[0].year));
    }
  }, [orderedYearlyTotals, selectedYear]);

  useEffect(() => {
    if (!showYearPopup) return;

    const onClickOutside = (event) => {
      if (!yearPopupRef.current) return;
      if (!yearPopupRef.current.contains(event.target)) {
        setShowYearPopup(false);
      }
    };

    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [showYearPopup]);

  useEffect(() => {
    // Trigger fade-in after mount
    setTimeout(() => setIsVisible(true), 10);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    if (typeof onClose === "function") {
      setTimeout(onClose, 300);
    }
  };

  if (!swimmer) return null;

  return (
    <div
      className="swimmer-tracker-backdrop"
      style={{
        opacity: isVisible ? 1 : 0,
        pointerEvents: isVisible ? "auto" : "none",
        transition: "opacity 300ms ease-in-out",
      }}
      onClick={handleClose}
    >
      <div
        className="swimmer-tracker-modal"
        style={{
          transform: isVisible
            ? "translateY(0) scale(1)"
            : "translateY(-20px) scale(0.985)",
          transition: "transform 300ms ease, opacity 300ms ease",
          opacity: isVisible ? 1 : 0,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="swimmer-tracker-close" onClick={handleClose}>
          ×
        </button>

        <div className="swimmer-tracker-header">
          <div className="swimmer-tracker-header-block">
            <h2 className="swimmer-tracker-title">{swimmer.name}</h2>
            <div className="swimmer-tracker-header-meta-wrap">
              <span className="swimmer-tracker-header-pill">
                Coach: {swimmer.coach || "Unassigned"}
              </span>
              <span className="swimmer-tracker-header-pill">
                Age: {swimmer.age ?? "—"}
              </span>
              <span className="swimmer-tracker-header-pill">
                Projected Total: {formatPoints(projectedSeasonPoints)}
              </span>
            </div>
          </div>
        </div>

        <div className="swimmer-tracker-summary-row">
          <div className="swimmer-tracker-summary-card">
            <strong>{formatPercent(swimmer.attendance_percentage)}</strong>
            <span>Meet Attendance</span>
            <small className="swimmer-tracker-summary-subtext">
              {priorYear
                ? `${priorYear}: ${attendanceByYear[priorYear] || 0} meets`
                : "No prior-year attendance yet"}
            </small>
          </div>
          <div className="swimmer-tracker-summary-card">
            <strong>{formatPoints(projectedMeetPoints)}</strong>
            <span>Projected Meet Points</span>
          </div>
          <div className="swimmer-tracker-summary-card">
            <strong>{swimmer.meets_attended || 0}</strong>
            <span>Meets Attended</span>
          </div>
          <div className="swimmer-tracker-summary-card">
            <strong>{formatSeconds(bestDropResult?.time_drop)}</strong>
            <span>Best Drop (Seconds)</span>
            <small className="swimmer-tracker-summary-subtext">
              Drop points: {formatPoints(bestDropResult?.time_drop_points)}
              {bestDropResult ? ` • ${formatMeetDisplay(bestDropResult)}` : ""}
            </small>
          </div>
        </div>

        <div className="swimmer-tracker-section">
          <div className="swimmer-tracker-section-header">
            <h3 className="swimmer-tracker-section-title">
              Potential Points per Year
            </h3>
          </div>
          {orderedYearlyTotals.length ? (
            <div className="swimmer-tracker-year-chip-wrap" ref={yearPopupRef}>
              {orderedYearlyTotals.map((entry) => (
                <button
                  key={`${entry.year}`}
                  type="button"
                  onClick={() => {
                    const nextYear = String(entry.year);
                    if (showYearPopup && selectedYear === nextYear) {
                      setShowYearPopup(false);
                      return;
                    }
                    setSelectedYear(nextYear);
                    setShowYearPopup(true);
                  }}
                  className={
                    showYearPopup && String(selectedYear) === String(entry.year)
                      ? "swimmer-tracker-year-chip swimmer-tracker-year-chip-active"
                      : "swimmer-tracker-year-chip"
                  }
                >
                  <span className="swimmer-tracker-year-chip-year">
                    {entry.year}
                  </span>
                  <span className="swimmer-tracker-year-chip-points">
                    {formatPoints(entry.points)}
                  </span>
                  <span className="swimmer-tracker-year-chip-breakdown">
                    breakdown
                  </span>
                </button>
              ))}

              {showYearPopup ? (
                selectedYearMeetBreakdown.length ? (
                  <div className="swimmer-tracker-year-popup-card">
                    <strong className="swimmer-tracker-year-popup-title">
                      {selectedYear} breakdown
                    </strong>
                    <div className="swimmer-tracker-year-detail-list">
                      {selectedYearMeetBreakdown.map((meet) => (
                        <div
                          key={`year-detail-${selectedYear}-${meet.meet_id || meet.meet_label}`}
                          className="swimmer-tracker-year-detail-item"
                        >
                          <strong className="swimmer-tracker-year-detail-title">
                            {formatMeetName(meet)}
                          </strong>
                          <span className="swimmer-tracker-year-detail-meta">
                            Potential if active: {formatPoints(meet.raw_points)}{" "}
                            pts
                          </span>
                          <span className="swimmer-tracker-year-detail-meta">
                            Place: {formatPoints(meet.potential_place_points)} •
                            Drop: {formatPoints(meet.potential_drop_points)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="swimmer-tracker-year-popup-card">
                    <strong className="swimmer-tracker-year-popup-title">
                      {selectedYear} breakdown
                    </strong>
                    <p className="swimmer-tracker-empty-text">
                      No meet breakdown available for {selectedYear}.
                    </p>
                  </div>
                )
              ) : null}
            </div>
          ) : (
            <p className="swimmer-tracker-empty-text">No yearly totals yet.</p>
          )}
        </div>

        <div className="swimmer-tracker-section">
          <div className="swimmer-tracker-section-header">
            <h3 className="swimmer-tracker-section-title">Meet Trend</h3>
            <span className="swimmer-tracker-section-hint">
              Each point shows meet points and average drop/add (sec). Click a
              point for place vs drop details.
            </span>
          </div>
          <TrendChart meets={meetSummaries} />
        </div>

        <div className="swimmer-tracker-section">
          <h3 className="swimmer-tracker-section-title">Meet Summary</h3>
          {meetSummaries.length ? (
            <div className="swimmer-tracker-table-wrap">
              <table className="swimmer-tracker-table">
                <thead>
                  <tr>
                    <th className="swimmer-tracker-th">Meet</th>
                    <th className="swimmer-tracker-th">Date</th>
                    <th className="swimmer-tracker-th">Year</th>
                    <th className="swimmer-tracker-th">Events</th>
                    <th className="swimmer-tracker-th">Avg Drop (sec)</th>
                    <th className="swimmer-tracker-th">Best Drop (sec)</th>
                    <th className="swimmer-tracker-th">Potential Points</th>
                  </tr>
                </thead>
                <tbody>
                  {meetSummaries.map((meet) => (
                    <tr key={`meet-${meet.meet_id || meet.meet_label}`}>
                      <td className="swimmer-tracker-td">
                        <span className="swimmer-tracker-meet-name-strong">
                          {formatMeetName(meet)}
                        </span>
                      </td>
                      <td className="swimmer-tracker-td">
                        {formatMeetMonthDay(meet)}
                      </td>
                      <td className="swimmer-tracker-td">
                        {meet.meet_year ?? "—"}
                      </td>
                      <td className="swimmer-tracker-td">{meet.events}</td>
                      <td className="swimmer-tracker-td">
                        {formatDrop(meet.average_drop)}
                      </td>
                      <td className="swimmer-tracker-td">
                        {formatDrop(meet.best_drop)}
                      </td>
                      <td className="swimmer-tracker-td">
                        {formatPoints(meet.raw_points)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="swimmer-tracker-empty-text">
              No meet summaries available yet.
            </p>
          )}
        </div>

        <div className="swimmer-tracker-section">
          <h3 className="swimmer-tracker-section-title">Event Details</h3>
          {eventGroups.length ? (
            <div className="swimmer-tracker-event-grid">
              {eventGroups.map((group) => {
                const isTrendOpen = selectedEventTrendKey === group.eventKey;

                return (
                  <article
                    key={group.eventKey}
                    className="swimmer-tracker-event-card swimmer-tracker-event-card-open"
                  >
                    <button
                      type="button"
                      className="swimmer-tracker-event-card-header swimmer-tracker-event-card-header-btn"
                      onClick={() =>
                        setSelectedEventTrendKey((prev) =>
                          prev === group.eventKey ? "" : group.eventKey,
                        )
                      }
                    >
                      <span className="swimmer-tracker-event-name">
                        {group.eventName}
                      </span>
                      <span className="swimmer-tracker-event-best-time">
                        {formatTime(group.bestTime)}
                      </span>
                      <span className="swimmer-tracker-event-meta">
                        {group.entries.length} swims •{" "}
                        {isTrendOpen ? "Hide trend" : "Show trend"}
                      </span>
                    </button>

                    {isTrendOpen ? (
                      <EventTrendPopup entries={group.entries} />
                    ) : null}

                    <div className="swimmer-tracker-event-history-list">
                      {group.entries.map((entry) => (
                        <div
                          key={entry.id}
                          className="swimmer-tracker-event-history-item"
                        >
                          <div className="swimmer-tracker-event-history-toprow">
                            <strong className="swimmer-tracker-event-history-meet">
                              {entry.meetAbbreviation}
                            </strong>
                            <span className="swimmer-tracker-event-history-time">
                              {formatTime(entry.finalTime)}
                            </span>
                          </div>
                          <div className="swimmer-tracker-event-history-subrow">
                            <span className="swimmer-tracker-event-history-name">
                              {entry.meetName}
                            </span>
                            <span>
                              Base {formatTime(entry.baselineTime)} • Drop{" "}
                              {formatDrop(entry.drop)} • {entry.status === "Bench" ? "Potential Pts" : "Pts"}{" "}
                              {formatPoints(entry.points)} • {entry.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <p className="swimmer-tracker-empty-text">
              No event history available yet.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SwimmerTracker;
